import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Pagination } from 'antd';
import { UserRating } from '../../services/api';
import { PageRoute, RatingSortOption } from '../../models/enums';
import RatingModal from '../../components/RatingModal';
import SellerInfoCard from '../../components/SellerInfoCard';
import BreadcrumbBar from '../../components/BreadcrumbBar';
import HelmetComponent from '../../utils/HelmetHelper';
import RatingsFilters from '../../components/RatingsFilters';
import RatingCard from '../../components/RatingCard';
import styles from './ratingsSellerPage.scss';
import history from '../../history';
import { getCreatorInfo, selectSeller } from '../../routes/Ad/ad.ducks';
import { isMobileScreen, getWindowWidth, calculateTotalPagingCount, scrollToTop, notification } from '../../services';
import config from 'appConfig';
interface RatingsSellerPageProps {
  match: {
    params: {
      username: string;
    }
  };
  location?: {
    state?: {
      openRatingModal?: boolean;
      preselectedAdId?: string;
      preselectedChatId?: string;
      returnUrl?: string;
    }
  };
}

const RatingsSellerPage: React.FC<RatingsSellerPageProps> = ({ match, location }) => {
  const dispatch = useDispatch();
  const seller = useSelector(selectSeller);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(null);
  const [sortOption, setSortOption] = useState(RatingSortOption.Newest);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  const [returnUrl, setReturnUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [ratingCounts, setRatingCounts] = useState({ totalCount: 0, averageRating: null });
  const isMobile = isMobileScreen(getWindowWidth());
  const userId = seller ? seller.id : undefined;

  useEffect(() => {
    const username = match.params.username;
    if (username) {
      dispatch(getCreatorInfo(decodeURIComponent(username)));
    }
  }, [match.params.username, dispatch]);

  // Read filter from URL query parameters on mount
  useEffect(() => {
    if (location && location.search) {
      const params = new URLSearchParams(location.search);

      // Read type filter
      const typeParam = params.get('type');
      if (typeParam) {
        const typeValue = parseInt(typeParam, 10);
        if (typeValue === 1 || typeValue === 2) {
          setFilterType(typeValue);
        }
      }

      // Read sort option
      const sortParam = params.get('sortOption');
      if (sortParam) {
        const sortValue = parseInt(sortParam, 10);
        if (sortValue >= 1 && sortValue <= 4) {
          setSortOption(sortValue);
        }
      }
    }
  }, [location]);

  const fetchRatings = async () => {
    try {
      setLoading(true);

      const response = await UserRating.list({
        userId,
        type: filterType,
        page: page,
        itemPerPage: config.maxAdPerPage,
        sortOption: sortOption
      });

      setRatings(response.data || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };


  // Update URL query string when filters change
  useEffect(() => {
    if (!location) return;

    const params = new URLSearchParams();

    if (filterType !== null) {
      params.set('type', filterType.toString());
    }

    if (sortOption !== RatingSortOption.Newest) {
      params.set('sortOption', sortOption.toString());
    }

    const newSearch = params.toString();
    const currentSearch = location.search ? location.search.substring(1) : '';

    // Only update if the query string has changed
    if (newSearch !== currentSearch) {
      const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      history.replace(newPath);
    }
  }, [filterType, sortOption]);

  useEffect(() => {
    if (userId) {
      fetchRatings();
    }
  }, [userId, filterType, sortOption, page]);

  // Fetch rating counts
  useEffect(() => {
    const fetchRatingCounts = async () => {
      try {
        const response = await UserRating.getOverallCount(userId);
        setRatingCounts(response);
      } catch (error) {
        console.error('Error fetching rating counts:', error);
        setRatingCounts({ totalCount: 0, averageRating: null });
      }
    };

    if (userId) {
      fetchRatingCounts();
    }
  }, [userId]);

  // Open rating modal when navigating from ad page or chat
  useEffect(() => {
    if (location && location.state && location.state.openRatingModal) {
      setRatingModalVisible(true);

      // Store returnUrl for redirect after successful rating
      if (location.state.returnUrl) {
        setReturnUrl(location.state.returnUrl);
      }

      history.replace({
        pathname: history.location.pathname,
        state: {
          preselectedAdId: location.state.preselectedAdId,
          preselectedChatId: location.state.preselectedChatId
        }
      });
    }
  }, [location]);

  const handleAnswerSuccess = () => {
    notification.show('Odgovor je uspješno poslan');
    fetchRatings();
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    scrollToTop();
  };

  const handleFilterTypeChange = (value: number | null) => {
    setFilterType(value);
    setPage(1);
  };

  if (!seller) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <HelmetComponent
        title={`Ocjene korisnika: ${seller.username} | ${"INDEX OGLASI"}`}
        ogTitle={`Ocjene korisnika: ${seller.username} | ${"INDEX OGLASI"}`}
      />
      <div className={styles.container}>
        <BreadcrumbBar
          routeLinks={[
            {
              title: `${seller && seller.username}`,
              link: `${PageRoute.user}/${match.params.username}`
            },
            {
              title: "Ocjene",
              link: null
            }
          ]}
          category=""
          className={styles.breadcrumb}
        />

        <div className={styles.mainLayout}>
          {/* Left sidebar with seller info card */}
          <aside className={styles.sidebar}>
            <SellerInfoCard
              seller={seller}
              gaLabel="Ocijeni - stranica ocijena"
            />
          </aside>

          {/* Right content with filters and ratings */}
          <main className={styles.mainContent}>
            <RatingsFilters
              filterType={filterType}
              onFilterTypeChange={handleFilterTypeChange}
              sortOption={sortOption}
              onSortChange={setSortOption}
              averageRating={ratingCounts.averageRating}
              totalCount={ratingCounts.totalCount}
            />

            {loading ? (
              <div className={styles.loadingContainer}>
                <Spin size="large" />
              </div>
            ) : ratings.length === 0 ? (
              <div className={styles.emptyState}>
                Korisnik još nema ocjena.
              </div>
            ) : (
              <React.Fragment>
                <div className={styles.ratingsContainer}>
                  {ratings.map(rating => (
                    <RatingCard
                      key={rating.id}
                      rating={rating}
                      userProfile={seller}
                      onAnswerSuccess={handleAnswerSuccess}
                    />
                  ))}
                </div>
                {totalCount > config.maxAdPerPage || page > 1 ? (
                  <Pagination
                    total={calculateTotalPagingCount(totalCount, page)}
                    current={page}
                    pageSize={config.maxAdPerPage}
                    onChange={handlePageChange}
                    className={styles.pagination}
                  />
                ) : null}
              </React.Fragment>
            )}
          </main>
        </div>

        <RatingModal
          visible={ratingModalVisible}
          preselectedAdId={location && location.state && location.state.preselectedAdId}
          preselectedChatId={location && location.state && location.state.preselectedChatId}
          userId={userId}
          onCancel={() => setRatingModalVisible(false)}
          onSuccess={() => {
            setRatingModalVisible(false);
            notification.show(
              <div>
                <div>{"Hvala na ostavljenoj ocjeni!"}</div>
                <div>{"Ocjena će nakon provjere od strane administratora biti vidljiva drugim korisnicima, najkasnije u roku od 48 sati."}</div>
              </div>
            );

            // Redirect to returnUrl if provided, otherwise stay on page
            if (returnUrl) {
              history.push(returnUrl);
            } else {
              fetchRatings();
            }
          }}
        />
      </div>
    </React.Fragment>
  );
};

export default RatingsSellerPage;



// WEBPACK FOOTER //
// ./src/containers/RatingsSellerPage/index.tsx